/**
 * Says, on screen, that this is not Arabic.
 *
 * The pseudo-locale renders Arabic SCRIPT, so to a reader who does not
 * read Arabic it looks like a translated product. That is the one risk
 * the whole approach was chosen to avoid -- a machine pass was declined
 * precisely because it removes the signal that says "not translated
 * yet" -- so the signal is put back explicitly rather than left to
 * whoever is looking to work out.
 *
 * Rendered only in a dev build and only while the pseudo-locale is
 * active. In a production bundle the branch is dead code and Vite drops
 * it, so there is nothing here for a customer to reach.
 *
 * Not translated, and not routed through the catalogue: a sentence
 * saying "the catalogue is fake" must not itself come from the fake
 * catalogue.
 */
import { useTranslation } from "react-i18next";

import { isPseudoLocale } from "shared/i18n";

/**
 * Composed outside the JSX, and deliberately NOT from the catalogue.
 *
 * The rule that forbids literals in JSX exists to route screen copy
 * through `t()`. This sentence says the catalogue is fake; taking it
 * from the fake catalogue would render it in pseudo-Arabic, which is
 * the one string on the page that has to stay readable.
 */
const BANNER_TEXT =
  "Pseudo-Arabic: layout test text, not a translation. Dev builds only.";

export function PseudoLocaleBanner() {
  const { i18n } = useTranslation();
  if (!import.meta.env.DEV) return null;
  if (!isPseudoLocale(i18n.language)) return null;
  return (
    <p
      data-testid="pseudo-locale-banner"
      dir="ltr"
      className="bg-rag-redBg px-3 py-1 text-center text-xs font-semibold text-rag-red"
    >
      {BANNER_TEXT}
    </p>
  );
}
